import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from "@mui/icons-material/Save";
import { Box, CircularProgress, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EquipmentFormType } from "../../../types/forms/warehouse/equipment";
import EquipmentForm from "./EquipmentForm";
import { useAccountUsers } from "../../../hooks/Users";

interface NewEquipmentPanelProps {
  formId: string;
  isCreating: boolean;
  accountId: number;
  onCancel: () => void;
  onSubmit: (values: EquipmentFormType, images: File[]) => Promise<void>;
}

export default function NewEquipmentPanel({
  formId,
  isCreating,
  accountId,
  onCancel,
  onSubmit,
}: Readonly<NewEquipmentPanelProps>) {
  const { t } = useTranslation();
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const users = useAccountUsers([accountId], true);

  return (
    <Paper sx={{ p: 3, m: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          {t("warehouse.equipment.add-new")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={t("generic.cancel")}>
            <IconButton size="small" color="error" onClick={onCancel} disabled={isCreating}>
              <EditOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("generic.save")}>
            <IconButton size="small" color="success" type="submit" form={formId} disabled={isCreating}>
              {isCreating ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <EquipmentForm
        formId={formId}
        isEditing={true}
        isSaving={isCreating}
        isDeleting={false}
        pendingImages={pendingImages}
        onAddPendingImage={(file) => setPendingImages((prev) => [...prev, file])}
        onRemovePendingImage={(index) => setPendingImages((prev) => prev.filter((_, i) => i !== index))}
        users={users.users}
        isLoadingUsers={users.isLoading}
        onSubmit={(values) => onSubmit(values, pendingImages)}
      />
    </Paper>
  );
}